/**
 * Repère des questions « placer_points_2d » : cadre, quadrillage sans axes et
 * décor (décisions prof 2026-09-24, gen_187 « Construire l'image d'une
 * figure »). Partagé par les trois dessins du format : la passation
 * (Repere2DInteractiveEleve), l'aperçu prof (Repere2DRenderer) et
 * l'impression (renderRepere2D).
 *
 *   repere.quadrillage : true → un quadrillage à carreaux CARRÉS, sans axes,
 *     sans graduations ni coordonnées (on compte des carreaux) ; absent : le
 *     repère d'avant, au pixel près.
 *   contenu.decor : { polygones: [['P', 'Q', 'R']], fleches: [['A', 'B']] } :
 *     des figures et des flèches entre points de dejaPlaces, dessinées d'office.
 *   contenu.relier : ["P'", "Q'", "R'"] : les points à placer reliés dans cet
 *     ordre dès que deux voisins sont placés, la figure fermée au dernier.
 */

const W = 600;
const REPERE = { padL: 52, padR: 26, padT: 26, padB: 46, plotH: 340 };
const QUADRILLAGE = { padL: 22, padR: 34, padT: 22, padB: 22 };

/** Le cadre du dessin (viewBox 600 de large) ; en quadrillage, des carreaux carrés. */
export const cadreRepere = (repere) => {
    const { xMin = 0, yMin = 0, xMax = 10, yMax = 10, xStep = 1, yStep = 1, quadrillage = false } = repere || {};
    if (quadrillage === true && xMax > xMin && yMax > yMin && xStep > 0 && yStep > 0) {
        const { padL, padR, padT, padB } = QUADRILLAGE;
        const plotW = W - padL - padR;
        const plotH = (plotW / ((xMax - xMin) / xStep)) * ((yMax - yMin) / yStep);
        return { W, H: padT + plotH + padB, padL, padR, padT, padB, plotW, plotH, quadrillage: true };
    }
    const { padL, padR, padT, padB, plotH } = REPERE;
    return { W, H: padT + plotH + padB, padL, padR, padT, padB, plotW: W - padL - padR, plotH, quadrillage: false };
};

/** Les segments du décor, en coordonnées du repère : côtés des polygones (fermés) et flèches. */
export const decorSegments = (decor, dejaPlaces = []) => {
    const pos = {};
    (dejaPlaces || []).forEach((p) => { if (p && p.label != null) pos[p.label] = p; });
    const seg = (a, b) => (pos[a] && pos[b] ? { x1: pos[a].x, y1: pos[a].y, x2: pos[b].x, y2: pos[b].y } : null);
    const polygones = Array.isArray(decor?.polygones) ? decor.polygones : [];
    const fleches = Array.isArray(decor?.fleches) ? decor.fleches : [];
    return {
        cotes: polygones.flatMap((poly) => (Array.isArray(poly) ? poly.map((l, i) => seg(l, poly[(i + 1) % poly.length])) : [])).filter(Boolean),
        fleches: fleches.map((f) => (Array.isArray(f) ? seg(f[0], f[1]) : null)).filter(Boolean),
    };
};

/**
 * Les côtés de la figure à construire : deux points voisins de `relier`, tous
 * deux placés, sont reliés ; le dernier côté ferme la figure quand tous le
 * sont (trois points au moins). `places` : { label → {x, y} }.
 */
export const segmentsRelies = (relier, places) => {
    if (!Array.isArray(relier) || relier.length < 2 || !places) return [];
    const n = relier.length;
    const ferme = n >= 3 && relier.every((l) => places[l]);
    const out = [];
    for (let i = 0; i < (ferme ? n : n - 1); i++) {
        const a = places[relier[i]]; const b = places[relier[(i + 1) % n]];
        if (a && b) out.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }
    return out;
};

const unite = (x, y) => { const n = Math.hypot(x, y); return n ? { x: x / n, y: y / n } : null; };

/** Côté où écrire le nom de chaque sommet d'un polygone (3 sommets placés au moins) : vers l'extérieur. */
export const directionsPolygone = (labels, pos) => {
    const noms = (Array.isArray(labels) ? labels : []).filter((l) => pos && pos[l]);
    if (noms.length < 3) return {};
    const g = { x: noms.reduce((s, l) => s + pos[l].x, 0) / noms.length, y: noms.reduce((s, l) => s + pos[l].y, 0) / noms.length };
    const out = {};
    noms.forEach((l) => { const u = unite(pos[l].x - g.x, pos[l].y - g.y); if (u) out[l] = u; });
    return out;
};

/**
 * Côté où écrire le nom des points du décor : hors de la figure pour ses
 * sommets, derrière le départ et au-delà de la pointe pour une flèche (vu en
 * PNG : le nom à droite du point passait sous la pointe de la flèche).
 */
export const directionsDecor = (decor, dejaPlaces = []) => {
    const pos = {};
    (dejaPlaces || []).forEach((p) => { if (p && p.label != null) pos[p.label] = p; });
    const out = {};
    (Array.isArray(decor?.polygones) ? decor.polygones : []).forEach((poly) => {
        // un « polygone » de deux sommets est un segment : chaque nom au-delà de son bout
        // (2026-09-24, 186 « Le troisième sommet » : le nom de A' passait sur le segment [A'B'])
        if (Array.isArray(poly) && poly.length === 2 && pos[poly[0]] && pos[poly[1]]) {
            const u = unite(pos[poly[1]].x - pos[poly[0]].x, pos[poly[1]].y - pos[poly[0]].y);
            if (u) { out[poly[0]] = { x: -u.x, y: -u.y }; out[poly[1]] = u; }
            return;
        }
        Object.assign(out, directionsPolygone(poly, pos));
    });
    (Array.isArray(decor?.fleches) ? decor.fleches : []).forEach((f) => {
        if (!Array.isArray(f) || !pos[f[0]] || !pos[f[1]]) return;
        const u = unite(pos[f[1]].x - pos[f[0]].x, pos[f[1]].y - pos[f[0]].y);
        if (!u) return;
        out[f[0]] = { x: -u.x, y: -u.y };
        out[f[1]] = u;
    });
    return out;
};

/** Le nom d'un point écrit dans la direction d (y du repère vers le haut) : position de la ligne de base et ancrage. */
export const etiquetteDirigee = (px, py, d, ecart, taille) => ({
    x: px + d.x * ecart,
    y: py - d.y * ecart + taille * 0.35,
    ancre: d.x > 0.35 ? 'start' : d.x < -0.35 ? 'end' : 'middle',
});

/**
 * Une flèche en pixels, de (x1, y1) vers (x2, y2) : le trait et la pointe,
 * arrêtée à `retrait` pixels du point d'arrivée (le point reste visible).
 */
export const flechePx = (x1, y1, x2, y2, { taille = 12, retrait = 6 } = {}) => {
    const len = Math.hypot(x2 - x1, y2 - y1) || 1;
    const ux = (x2 - x1) / len; const uy = (y2 - y1) / len;
    const tx = x2 - ux * retrait; const ty = y2 - uy * retrait;
    const bx = tx - ux * taille; const by = ty - uy * taille;
    const d = taille * 0.45;
    const f = (v) => v.toFixed(1);
    return {
        trait: { x1, y1, x2: bx, y2: by },
        pointe: `${f(tx)},${f(ty)} ${f(bx - uy * d)},${f(by + ux * d)} ${f(bx + uy * d)},${f(by - ux * d)}`,
    };
};
